import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import ContactDetailsFilter from './ContactDetailsFilter';
import { matchesContactFilter, normalizeContactFilter } from './contactFilterRules';
import { history } from "../../redux/store/configureStore";
import StudentsList from "./StudentsList";
import * as clientActions from "../../redux/actions/clientActions";
import * as studentActions from "../../redux/actions/studentActions";
import { filterArray } from "../common/Helpers";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";
import { ROLES } from "../common/Const";
import { getEffectivePermissionRole } from "./rbac";

export function Students({
  clients,
  students,
  loadClients,
  loadStudents,
  userRole,
  peopleViewState,
  onPeopleViewStateChange,
}) {
  const [contactFilter, setContactFilter] = useState(normalizeContactFilter(peopleViewState && peopleViewState.contactFilter));
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const canReadClientRecords = userRole !== ROLES.TEACHER;
  const [search, setSearch] = useState(peopleViewState.search || "");
  const [filteredStudents, setFilteredStudents] = useState(students);
  const [showArchivedStudents, setShowArchivedStudents] = useState(Boolean(peopleViewState.showArchived));

  useEffect(() => {
    if (canReadClientRecords && clients.length === 0) {
      loadClients().catch((error) => {
        alert(`Loading clients failed${error}`);
      });
    }

    if (students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed${error}`);
      });
    }
  }, [canReadClientRecords, clients.length, loadClients, loadStudents, students.length]);

  useEffect(() => {
    const filtered = students.filter((student) => {
      const shouldShowArchived = showArchivedStudents ? student.archived : !student.archived;
      return !student.deleted && shouldShowArchived;
    }).map((s) => {
      const client = s.clientId && clients ? clients.find(c => c.id === s.clientId) : null;
      const clientName = s.clientDisplayName || (client ? `${client.firstName} ${client.lastName}` : "");
      return {...s, clientName};
    });
    setFilteredStudents(filtered);
  }, [students, showArchivedStudents, clients]);
  const handleEdit = (student) => {
    history.push(buildManageRouteWithSchoolYear(`/manage/student/${student.id}`, "?returnTo=students", activeSchoolYearStartYear));
  };

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearch(value);
    onPeopleViewStateChange({ search: value });
  };

  const handleShowArchivedStudents = () => {
    const nextValue = !showArchivedStudents;
    setShowArchivedStudents(nextValue);
    onPeopleViewStateChange({ showArchived: nextValue });
  };

  const filteredStudentsArray = filterArray(search, filteredStudents)
    .filter(person => !canReadClientRecords || matchesContactFilter(person, contactFilter));

  const handleContactFilter = value => {
    setContactFilter(value);
    onPeopleViewStateChange({ contactFilter: value });
  };
  const clearFilters = () => {
    setSearch('');
    setContactFilter('all');
    setShowArchivedStudents(false);
    onPeopleViewStateChange({ search: '', contactFilter: 'all', showArchived: false });
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title col-block mb-0">Lista studentów</h2>
      <div className="contact-toolbar">
        <input
          name="search"
          className="form-control contact-search"
          value={search}
          onChange={handleSearchChange}
          placeholder="szukaj"
        />
      {canReadClientRecords && (
        <ContactDetailsFilter value={contactFilter} onChange={handleContactFilter} onClear={clearFilters} empty={filteredStudentsArray.length === 0} />
      )}
        <div>
          <label className="contact-archive"><input type="checkbox" checked={showArchivedStudents} onChange={handleShowArchivedStudents} />Archiwum</label>
        </div>
      </div>

      <StudentsList
        students={filteredStudentsArray}
        showContactDetails={canReadClientRecords}
        onEdit={userRole === ROLES.TEACHER ? null : handleEdit}
        hideClientIdFallback={userRole === ROLES.TEACHER}
        initialNumToShow={peopleViewState.numToShow}
        onNumToShowChange={numToShow => onPeopleViewStateChange({ numToShow })}
      />
    </div>
  );
}

Students.propTypes = {
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  loadClients: PropTypes.func.isRequired,
  loadStudents: PropTypes.func.isRequired,
  userRole: PropTypes.string,
  peopleViewState: PropTypes.shape({
    search: PropTypes.string,
    showArchived: PropTypes.bool,
    numToShow: PropTypes.number,
    contactFilter: PropTypes.string,
  }),
  onPeopleViewStateChange: PropTypes.func,
};

const mapStateToProps = state => ({
  clients: state.clients,
  students: state.students.filter(student => !student.deleted),
  userRole: getEffectivePermissionRole(state.user),
});

const mapDispatchToProps = dispatch => ({
  loadClients: () => dispatch(clientActions.loadClients()),
  loadStudents: () => dispatch(studentActions.loadStudents()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Students);

Students.defaultProps = {
  userRole: "",
  peopleViewState: { search: "", showArchived: false, numToShow: 25 },
  onPeopleViewStateChange: () => {},
};
